import { useCallback } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type GalleryControlsSize = "small" | "large";

export type GalleryControlsProps = {
    showCounter?: boolean;
    currentCount?: number;
    totalCount?: number;
    onPrevious: () => void;
    onNext: () => void;
    isFirstItemVisible?: boolean;
    isLastItemVisible?: boolean;
    size?: GalleryControlsSize;
};

const arrowCss = css({
    flexShrink: 0
});

const containerCss = css({
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: token("space.100")
});

const buttonBaseCss = css({
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "border": "none",
    "borderRadius": "100%",
    "cursor": "pointer",
    "flexShrink": 0,
    "flexDirection": "column",
    "backgroundColor": wacToken("color.primary.black"),
    "color": wacToken("color.neutral.light0"),
    "opacity": 1,
    "width": "40px",
    "height": "40px",
    "padding": "13px 10px",
    "&:hover": {
        backgroundColor: wacToken("color.secondary.navy")
    },
    "&:active": {
        backgroundColor: wacToken("color.secondary.navy"),
        transform: "scale(0.97)"
    },
    "&:disabled": {
        backgroundColor: `${wacToken("color.primary.black")} !important`,
        opacity: 0.3,
        cursor: "default"
    },
    "&:focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});
const buttonLargeCss = css({
    [media.above.md]: {
        width: "60px",
        height: "60px",
        padding: "19.5px 15px"
    }
});

const counterCss = css({
    minWidth: "40px",
    textAlign: "center",
    userSelect: "none"
});

export const GalleryControls = ({
    showCounter = false,
    currentCount,
    totalCount,
    onPrevious,
    onNext,
    isFirstItemVisible,
    isLastItemVisible,
    size
}: GalleryControlsProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const fireGalleryControlClicked = useCallback(
        (direction: "previous" | "next") => {
            createAnalyticsEvent({
                action: "clicked",
                actionSubject: "galleryControlButton",
                eventType: "ui",
                eventComponent: "button",
                source: "gallery-controls",
                attributes: {
                    direction
                }
            }).fire();
        },
        [createAnalyticsEvent]
    );

    const handlePreviousClick = useCallback(() => {
        fireGalleryControlClicked("previous");
        onPrevious();
    }, [fireGalleryControlClicked, onPrevious]);

    const handleNextClick = useCallback(() => {
        fireGalleryControlClicked("next");
        onNext();
    }, [fireGalleryControlClicked, onNext]);

    const isLarge = size === "large";
    const counterTypographyClass = size === "small" ? "body-sm" : "body-md";
    const isAtFirstItem = currentCount !== undefined && currentCount < 2;
    const isAtLastItem =
        totalCount !== undefined &&
        currentCount !== undefined &&
        currentCount >= totalCount;

    return (
        <div css={containerCss} role="group" aria-label="Gallery navigation">
            <button
                type="button"
                css={[buttonBaseCss, isLarge && buttonLargeCss]}
                onClick={handlePreviousClick}
                disabled={isFirstItemVisible || isAtFirstItem}
                aria-label="Previous"
                data-testid="gallery-controls-previous"
            >
                <svg
                    css={arrowCss}
                    viewBox="0 0 29 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    style={{ transform: "scaleX(-1)" }}
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.5361 19.1287C19.6575 20.0074 18.2322 20.0074 17.3535 19.1287C16.4748 18.25 16.4748 16.8248 17.3535 15.9461L21.248 12.0525L2.25 12.0525C1.00754 12.0523 0 11.045 0 9.80254C0.000234604 8.56023 1.00769 7.55276 2.25 7.55254L21.0654 7.55254L17.3535 3.84063C16.475 2.96202 16.4751 1.53765 17.3535 0.658987C18.2322 -0.219692 19.6575 -0.219692 20.5361 0.658987L27.8291 7.95293C28.4154 8.35896 28.8006 9.03537 28.8008 9.80254C28.8008 10.2822 28.6493 10.7265 28.3936 11.0916C28.2867 11.3125 28.1433 11.5206 27.96 11.7039L20.5361 19.1287Z"
                        fill="currentColor"
                    />
                </svg>
            </button>
            {showCounter && (
                <span
                    css={counterCss}
                    className={counterTypographyClass}
                    aria-live="polite"
                    aria-atomic="true"
                    data-testid="gallery-controls-counter"
                >
                    {currentCount}/{totalCount}
                </span>
            )}
            <button
                type="button"
                css={[buttonBaseCss, isLarge && buttonLargeCss]}
                onClick={handleNextClick}
                disabled={isLastItemVisible || isAtLastItem}
                aria-label="Next"
                data-testid="gallery-controls-next"
            >
                <svg
                    css={arrowCss}
                    viewBox="0 0 29 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.5361 19.1287C19.6575 20.0074 18.2322 20.0074 17.3535 19.1287C16.4748 18.25 16.4748 16.8248 17.3535 15.9461L21.248 12.0525L2.25 12.0525C1.00754 12.0523 0 11.045 0 9.80254C0.000234604 8.56023 1.00769 7.55276 2.25 7.55254L21.0654 7.55254L17.3535 3.84063C16.475 2.96202 16.4751 1.53765 17.3535 0.658987C18.2322 -0.219692 19.6575 -0.219692 20.5361 0.658987L27.8291 7.95293C28.4154 8.35896 28.8006 9.03537 28.8008 9.80254C28.8008 10.2822 28.6493 10.7265 28.3936 11.0916C28.2867 11.3125 28.1433 11.5206 27.96 11.7039L20.5361 19.1287Z"
                        fill="currentColor"
                    />
                </svg>
            </button>
        </div>
    );
};
